import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Descriptions, Divider, Flex } from 'antd';
import styled from 'styled-components';
import {
  Bleed,
  CodeBlock,
  ErrorAlertWithDrawer,
  Main,
  ObjectHeader,
  Padding,
  SidePanel,
} from '../../common/components';
import { TextSecondary } from '../../common/components/typography';
import { getDatePeriodString } from '../../common/utils';
import { SystemClassifierItem } from '../../systemClassifier/SystemClassifierItem';
import translations from '../algorithms/ValidationRuleViewPanel.translations.json';
import { ValidationRuleValidDataType } from '../schema';
import { ValidationStatus } from './ValidationStatus';
import { ValidationRuleTestRunModal } from './ValidationRuleTestRunModal';
import { useState } from 'react';
import { useCheckValidationRule } from './useCheckValidationRule';
import { ValidationRuleErrorDetails } from './ValidationRuleErrorDetails';

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-rule-view-panel',
);
addTranslations(translations);

export interface ValidationRuleViewPanelProps {
  onClose: () => void;
  validationRule: ValidationRuleValidDataType;
}

export function ValidationRuleViewPanel({
  onClose,
  validationRule,
}: ValidationRuleViewPanelProps) {
  const { t } = useTranslation();

  const [testRunData, setTestRunData] =
    useState<ValidationRuleValidDataType | null>(null);
  const { handleCheck, mutationErrors, isChecking } =
    useCheckValidationRule(validationRule);

  return (
    <SidePanel
      side="right"
      minWidth={700}
      collapsedWidth={700}
      width={1786}
      mode="layered"
    >
      <SidePanelWrapper>
        <AlertWrapper>
          <ErrorAlertWithDrawer
            errorAction={t('action.validating')}
            validationMessagesMap={mutationErrors}
            renderErrorMeta={(_, messages, index) => (
              <ValidationRuleErrorDetails index={index} messages={messages} />
            )}
            errorPrewrap
          />
        </AlertWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <ObjectHeader
              upperText={validationRule.code}
              lowerText={t('validationRule.heading.label')}
            />
            <ValidationStatus itemId={validationRule.statusId} />
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <Scrollable>
          <ScrollContent>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'groupCode',
                  label: t('validationRule.groupCode.label'),
                  children: validationRule.groupCode,
                },
                {
                  key: 'validPeriod',
                  label: t('validationRule.validPeriod.label'),
                  children: getDatePeriodString(
                    validationRule.validPeriod?.[0],
                    validationRule.validPeriod?.[1],
                  ),
                },
                {
                  key: 'errorDescription',
                  label: t('validationRule.errorDescription.label'),
                  children: validationRule.errorDescription,
                },
              ]}
            />
            <Bleed direction="horizontal">
              <Divider />
            </Bleed>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'errorLevelId',
                  label: t('validationRule.errorLevelId.label'),
                  children: (
                    <SystemClassifierItem
                      code="VALIDATION_ERROR_LEVEL"
                      itemId={validationRule.errorLevelId}
                    />
                  ),
                },
                {
                  key: 'ignoreError',
                  label: t('validationRule.ignoreError.label'),
                  children: validationRule.ignoreError
                    ? t('validationRule.ignoreError.yes')
                    : t('validationRule.ignoreError.no'),
                },
                {
                  key: 'tolerance',
                  label: t('validationRule.tolerance.label'),
                  children: validationRule.tolerance,
                },
                {
                  key: 'ignoreReason',
                  label: t('validationRule.ignoreReason.label'),
                  children: validationRule.ignoreReason,
                },
              ]}
            />
            <Bleed direction="horizontal">
              <Divider />
            </Bleed>
            <Flex vertical gap="middle">
              <TextField value={validationRule.condition} prop="condition" />
              <TextField
                value={validationRule.expressionLeft}
                prop="expressionLeft"
              />
              <Descriptions
                column={1}
                items={[
                  {
                    key: 'expressionOperatorId',
                    label: t('validationRule.expressionOperatorId.label'),
                    children: (
                      <SystemClassifierItem
                        code="VALIDATION_RULE_OPERATOR"
                        itemId={validationRule.expressionOperatorId}
                      />
                    ),
                  },
                ]}
              />
              <TextField
                value={validationRule.expressionRight}
                prop="expressionRight"
              />
            </Flex>
          </ScrollContent>
        </Scrollable>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button
              id="test-run-validation-rule"
              onClick={() => setTestRunData(validationRule)}
            >
              {t('button.testRun')}
            </Button>
            <Button
              id="check-validation-rule"
              loading={isChecking}
              onClick={handleCheck}
            >
              {t('button.check')}
            </Button>
            <Button id="close-validationRule" onClick={onClose}>
              {t('button.close')}
            </Button>
          </FullWidthFlex>
          {testRunData && (
            <ValidationRuleTestRunModal
              validationRule={testRunData}
              onClose={() => setTestRunData(null)}
            />
          )}
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

const AlertWrapper = styled.div`
  max-height: 72px;
  overflow-y: auto;
`;

const Scrollable = styled.div`
  overflow-y: auto;
`;
const ScrollContent = styled(Padding)`
  overflow-x: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;

const TextField = ({ value, prop }: { value: string | null; prop: string }) => {
  const { t } = useTranslation();

  return (
    <Flex vertical gap="small">
      <TextSecondary>
        <small>{t(`validationRule.${prop}.label`)}</small>
      </TextSecondary>
      <CodeBlock>{value}</CodeBlock>
    </Flex>
  );
};
